import { useMemo } from 'react';
import { useAuthReady } from './useAuthReady';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSwapOrder,
  estimateSwap,
  fetchSwapAssets,
  fetchSwapConfig,
  fetchSwapOrder,
  fetchSwapOrders,
  isSwapPending,
  linkSwapDeposit,
  swapAssetKey,
  type SwapEstimate,
} from '../lib/swap';
import { SWAPPABLE_COINS, coinKey, type Coin } from '../lib/coins';
import type { Chain, Token } from '../lib/transfers';

const DEFAULT_ASSETS_STALE_MS = 5 * 60_000;

export function useSwapConfig() {
  const { canFetch: ready, initData } = useAuthReady();
  return useQuery({
    queryKey: ['swap-config'],
    enabled: ready,
    staleTime: 60_000,
    queryFn: () => fetchSwapConfig(initData),
  });
}

/**
 * Live provider assets ∩ local catalogue. Client caches for 5m (server
 * ChangeHero currencies cache is 10m) and refreshes on focus.
 */
export function useSwapAssets() {
  const { canFetch: ready, initData } = useAuthReady();
  const q = useQuery({
    queryKey: ['swap-assets'],
    enabled: ready,
    staleTime: DEFAULT_ASSETS_STALE_MS,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: true,
    queryFn: () => fetchSwapAssets(initData),
  });

  const liveKeys = useMemo(() => {
    const set = new Set<string>();
    for (const a of q.data?.assets ?? []) set.add(swapAssetKey(a));
    return set;
  }, [q.data?.assets]);

  const coins: Coin[] = useMemo(() => {
    // Until the first live snapshot, do not offer the static catalogue alone —
    // that is how users hit dead-end quotes for temporarily disabled tickers.
    if (!q.data) return [];
    return SWAPPABLE_COINS.filter((c) => liveKeys.has(coinKey(c)));
  }, [liveKeys, q.data]);

  return {
    coins,
    liveKeys,
    loading: q.isLoading,
    error: q.isError,
    /** True once we have a successful live snapshot (possibly stale/cached). */
    ready: !!q.data,
    refetch: q.refetch,
  };
}

export function useSwapOrders(filter?: { chain?: Chain; token?: Token }) {
  const { canFetch: ready, initData } = useAuthReady();
  const qc = useQueryClient();

  const ordersQ = useQuery({
    queryKey: filter
      ? ['swap-orders', filter.chain ?? '', filter.token ?? '']
      : ['swap-orders'],
    enabled: ready,
    refetchInterval: (q) => {
      const orders = q.state.data ?? [];
      return orders.some((o) => isSwapPending(o.status)) ? 8_000 : false;
    },
    queryFn: () => fetchSwapOrders(initData, filter),
  });

  const refreshOrder = async (partnerOrderId: string) => {
    const order = await fetchSwapOrder(initData, partnerOrderId);
    qc.setQueryData(['swap-orders'], (prev: typeof ordersQ.data) => {
      if (!prev) return [order];
      const i = prev.findIndex((o) => o.partnerOrderId === partnerOrderId);
      if (i < 0) return [order, ...prev];
      const next = prev.slice();
      next[i] = order;
      return next;
    });
    return order;
  };

  return {
    orders: ordersQ.data ?? [],
    loading: ordersQ.isLoading,
    refreshOrder,
    invalidate: () => qc.invalidateQueries({ queryKey: ['swap-orders'] }),
  };
}

export function useSwapActions() {
  const { canFetch: ready, initData } = useAuthReady();
  const qc = useQueryClient();

  const estimate = useMutation({
    mutationFn: async (body: {
      fromChain: Chain;
      fromToken: Token;
      toChain: Chain;
      toToken: Token;
      fromAmount: string;
    }): Promise<SwapEstimate> => {
      if (!ready) throw new Error('not_ready');
      return estimateSwap(initData, body);
    },
  });

  const createOrder = useMutation({
    mutationFn: async (body: {
      fromChain: Chain;
      fromToken: Token;
      toChain: Chain;
      toToken: Token;
      fromAmount: string;
    }) => {
      if (!ready) throw new Error('not_ready');
      return createSwapOrder(initData, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['swap-orders'] });
    },
  });

  const linkDeposit = useMutation({
    mutationFn: async (args: { partnerOrderId: string; depositTransferId: string }) => {
      if (!ready) throw new Error('not_ready');
      return linkSwapDeposit(initData, args.partnerOrderId, args.depositTransferId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['swap-orders'] });
      qc.invalidateQueries({ queryKey: ['transfers'] });
    },
  });

  return { estimate, createOrder, linkDeposit };
}
